import jsPDF from 'jspdf';
import QRCode from 'qrcode';

/**
 * Genera un PDF con información del producto incluyendo código QR
 * @param {Object} producto - Información del producto
 * @param {string} producto.codbar - Código del producto (código de barras)
 * @param {string} producto.descripcion - Descripción del producto
 * @param {number} producto.cantidad - Cantidad del producto
 * @param {number} producto.peso - Peso del producto
 * @param {string} [producto.lote] - Lote del producto (opcional)
 * @param {string} [producto.fechaVencimiento] - Fecha de vencimiento (opcional)
 * @param {string} [producto.FechaIngreso] - Fecha de ingreso (opcional)
 * @param {string} [producto.proveedor] - Proveedor (opcional)
 * @param {Object} [opciones] - Opciones adicionales para el PDF
 * @param {string} [opciones.titulo] - Título del documento
 * @param {boolean} [opciones.mostrarFecha] - Mostrar fecha actual
 * @param {string} [opciones.logoUrl] - URL del logo (opcional)
 */

// ─── Helper: resuelve orientación y formato para jsPDF ───────────────────────
// jsPDF siempre quiere [lado_corto, lado_largo] + la orientación correcta.
// Si el usuario ingresó ancho > alto → landscape; si no → portrait.
const resolverOrientacionYFormato = (format, orientation) => {
  if (!Array.isArray(format)) return { format, orientation };
  const [dim1, dim2] = format;
  const esLandscape = dim1 > dim2;
  return {
    format: [Math.min(dim1, dim2), Math.max(dim1, dim2)],
    orientation: esLandscape ? 'landscape' : 'portrait',
    _esLandscape: esLandscape,          // flag extra para el layout
    _anchoReal: Math.max(dim1, dim2),   // ancho real que quiso el usuario
    _altoReal:  Math.min(dim1, dim2),   // alto  real que quiso el usuario
  };
};



export const generarPDFConQR = async (producto, opciones = {}) => {
  try {
    // ── Configuración ──────────────────────────────────────────────────────
    const config = {
      titulo:       opciones.titulo       || 'Información del Producto',
      mostrarFecha: opciones.mostrarFecha !== false,
      logoUrl:      opciones.logoUrl      || null,
      format:       opciones.format       || 'a4',
      orientation:  opciones.orientation  || 'portrait',
      unit:         opciones.unit         || 'mm',
      ...opciones,
    };

    // ── Crear PDF con orientación y formato correctos ──────────────────────
    const { format: formatFinal, orientation: orientFinal, _esLandscape } =
      resolverOrientacionYFormato(config.format, config.orientation);

    const pdf = new jsPDF({ orientation: orientFinal, unit: config.unit, format: formatFinal });
    pdf.setFont('helvetica');

    // ── Dimensiones reales post-creación ────────────────────────────────────
    const pageWidth  = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // ── Clasificación del tamaño ────────────────────────────────────────────
    const esPersonalizada    = Array.isArray(config.format);
    const esLandscapeChica   = esPersonalizada && _esLandscape;
    const esPortraitChica    = esPersonalizada && !_esLandscape;

    // ── Generar QR ──────────────────────────────────────────────────────────
    let qrDataURL = '';
    if (producto.codbar) {
      try {
        qrDataURL = await QRCode.toDataURL(String(producto.codbar), {
          width: 200, margin: 2, color: { dark: '#000', light: '#FFF' },
        });
      } catch (e) { console.error('Error generando QR:', e); }
    }

    // ══════════════════════════════════════════════════════════════════════════
    // LAYOUT A: Etiqueta horizontal/landscape personalizada
    //   QR a la izquierda | texto a la derecha, todo en una fila
    // ══════════════════════════════════════════════════════════════════════════
    if (esLandscapeChica) {
      const mg       = 2;
      const altoUtil = pageHeight - mg * 2;
      // QR ocupa el alto disponible (es cuadrado) pero no más del 40% del ancho
      const qrSize   = Math.min(altoUtil, pageWidth * 0.40);
      const qrX      = mg;
      const qrY      = mg;

      if (qrDataURL) {
        pdf.addImage(qrDataURL, 'PNG', qrX, qrY, qrSize, qrSize);
        pdf.setLineWidth(0.2);
        pdf.rect(qrX - 0.5, qrY - 0.5, qrSize + 1, qrSize + 1);
      }

      // Columna de texto a la derecha del QR
      const txtX    = qrX + qrSize + 3;
      const txtMaxW = pageWidth - txtX - mg;
      // Distribución dinámica: cuántas líneas podemos mostrar y cuánto espacio tienen
      const maxLineas  = 6;
      const lineH      = altoUtil / maxLineas;
      const fontSize   = Math.max(5, Math.min(8, lineH * 0.75));
      pdf.setFontSize(fontSize);

      let ty = mg + fontSize * 0.35;

      const writeLine = (label, value) => {
        if (value === undefined || value === null || value === '') return;
        const valueStr = String(value);
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, txtX, ty);
        const labelW = pdf.getTextWidth(label) + 1;
        pdf.setFont('helvetica', 'normal');
        const truncated = pdf.splitTextToSize(valueStr, txtMaxW - labelW)[0] || valueStr;
        pdf.text(truncated, txtX + labelW, ty);
        ty += lineH;
      };

      writeLine('QR:', producto.codbar);
      writeLine('Producto:', producto.descripcion);
      writeLine('Unidad:', producto.peso != null ? `${producto.peso} ${producto.unidad || ''}` : null);
      writeLine('Lote:', producto.lote);
      if (producto.fechaVencimiento) {
        const dt = producto.fechaVencimiento instanceof Date
          ? producto.fechaVencimiento
          : parseFechaFlexible(producto.fechaVencimiento);
        writeLine('Vence:', dt ? dt.toLocaleDateString('es-ES') : '—');
      }
      if (producto.proveedor) {
        const prov = typeof producto.proveedor === 'string'
          ? producto.proveedor
          : (producto.proveedor.sDescripcion || producto.proveedor.nombre || String(producto.proveedor));
        writeLine('Prov.:', prov);
      }

      return pdf;
    }

    // ══════════════════════════════════════════════════════════════════════════
    // LAYOUT B: Etiqueta vertical/portrait personalizada
    //   QR centrado arriba, texto compacto debajo
    // ══════════════════════════════════════════════════════════════════════════
    if (esPortraitChica) {
      const mg        = 2;
      const anchoUtil = pageWidth  - mg * 2;
      // QR ocupa hasta el 55% del alto o el 85% del ancho (lo que sea menor)
      const qrSize    = Math.min(anchoUtil * 0.85, pageHeight * 0.55);
      const qrX       = mg + (anchoUtil - qrSize) / 2; // centrado horizontalmente
      let yPos        = mg;

      if (qrDataURL) {
        pdf.addImage(qrDataURL, 'PNG', qrX, yPos, qrSize, qrSize);
        pdf.setLineWidth(0.2);
        pdf.rect(qrX - 0.5, yPos - 0.5, qrSize + 1, qrSize + 1);
      }
      yPos += qrSize + 2;

      // Línea separadora fina
      pdf.setLineWidth(0.3);
      pdf.line(mg, yPos, mg + anchoUtil, yPos);
      yPos += 1.5;

      const altoRestante = pageHeight - yPos - mg;
      const maxLineas    = 5;
      const lineH        = altoRestante / maxLineas;
      const fontSize     = Math.max(5, Math.min(8, lineH * 0.75));
      const labelW       = 15;
      pdf.setFontSize(fontSize);

      const writeLine = (label, value) => {
        if (value === undefined || value === null || value === '') return;
        if (yPos > pageHeight - mg) return;
        const valueStr = String(value);
        pdf.setFont('helvetica', 'bold');   pdf.text(label, mg, yPos);
        pdf.setFont('helvetica', 'normal');
        const maxW     = anchoUtil - labelW;
        const truncated = pdf.splitTextToSize(valueStr, maxW)[0] || valueStr;
        pdf.text(truncated, mg + labelW, yPos);
        yPos += lineH;
      };

      writeLine('QR:', producto.codbar);
      writeLine('Producto:', producto.descripcion);
      writeLine('Unidad:', producto.peso != null ? `${producto.peso} ${producto.unidad || ''}` : null);
      writeLine('Lote:', producto.lote);
      if (producto.fechaVencimiento) {
        const dt = producto.fechaVencimiento instanceof Date
          ? producto.fechaVencimiento
          : parseFechaFlexible(producto.fechaVencimiento);
        writeLine('Vence:', dt ? dt.toLocaleDateString('es-ES') : '—');
      }

      return pdf;
    }

    // ══════════════════════════════════════════════════════════════════════════
    // LAYOUT C: A4 / formato estándar (portrait o landscape A4)
    //   Layout completo con encabezado, QR en esquina derecha, pie de página
    // ══════════════════════════════════════════════════════════════════════════
    const margenIzquierdo = 20;
    const margenDerecho   = 20;
    const anchoLinea      = pageWidth - margenIzquierdo - margenDerecho;
    let yPos = 20;

    // Encabezado
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(config.titulo, margenIzquierdo, yPos);
    yPos += 15;

    // Fecha
    if (config.mostrarFecha) {
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const fechaActual = new Date().toLocaleDateString('es-ES', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
      pdf.text(`Fecha: ${fechaActual}`, margenIzquierdo, yPos);
      yPos += 10;
    }

    // Línea separadora
    pdf.setLineWidth(0.5);
    pdf.line(margenIzquierdo, yPos, margenIzquierdo + anchoLinea, yPos);
    yPos += 15;

    // QR (esquina derecha)
    const qrSize    = Math.min(50, pageWidth * 0.13);
    const qrX       = margenIzquierdo + anchoLinea - qrSize;
    const qrY       = yPos;
    if (qrDataURL) {
      pdf.addImage(qrDataURL, 'PNG', qrX, qrY, qrSize, qrSize);
      pdf.setLineWidth(0.3);
      pdf.rect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 4);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      const textoQR = 'Código QR';
      const textoW  = pdf.getTextWidth(textoQR);
      pdf.text(textoQR, qrX + qrSize / 2 - textoW / 2, qrY + qrSize + 12);
    }

    // Datos del producto (izquierda)
    pdf.setFontSize(12);
    const labelOffset = 25;

    const writeField = (label, value) => {
      if (value === undefined || value === null || value === '') return;
      pdf.setFont('helvetica', 'bold');
      pdf.text(label, margenIzquierdo, yPos);
      pdf.setFont('helvetica', 'normal');
      const lines = pdf.splitTextToSize(String(value), anchoLinea - qrSize - 15 - labelOffset);
      pdf.text(lines, margenIzquierdo + labelOffset, yPos);
      yPos += lines.length * 6 + 2;
    };

    writeField('Código QR:', producto.codbar);
    writeField('Código:',    producto.codigo);
    writeField('Producto:',  producto.descripcion);
    writeField('Unidad:',    producto.peso != null ? `${producto.peso} ${producto.unidad || ''}` : null);
    writeField('Lote:',      producto.lote);
    if (producto.fechaVencimiento) {
      const dt = producto.fechaVencimiento instanceof Date
        ? producto.fechaVencimiento
        : parseFechaFlexible(producto.fechaVencimiento);
      writeField('Vence:', dt ? dt.toLocaleDateString('es-ES') : '—');
    }
    if (producto.proveedor) {
      const prov = typeof producto.proveedor === 'string'
        ? producto.proveedor
        : (producto.proveedor.sDescripcion || producto.proveedor.nombre || String(producto.proveedor));
      writeField('Proveedor:', prov);
    }
    if (producto.proceso) writeField('Proceso:', producto.proceso);

    // Pie de página
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.text('Generado por PISeun - Sistema de Gestión', margenIzquierdo, pageHeight - 15);
    pdf.setLineWidth(0.3);
    pdf.line(margenIzquierdo, pageHeight - 20, margenIzquierdo + anchoLinea, pageHeight - 20);

    return pdf;

  } catch (error) {
    console.error('Error generando PDF:', error);
    throw new Error('Error al generar el PDF: ' + error.message);
  }
};


/**
 * Genera y descarga un PDF para un producto
 * @param {Object} producto - Información del producto
 * @param {Object} opciones - Opciones para el PDF
 * @param {string} [nombreArchivo] - Nombre del archivo a descargar
 */
export const descargarPDFProducto = async (producto, opciones = {}, nombreArchivo = null) => {
  try {
    const pdf = await generarPDFConQR(producto, opciones);

    // Generar nombre de archivo si no se proporciona
    const nombre = nombreArchivo ||
      `producto_${producto.codbar || 'sin_codbar'}_${new Date().getTime()}.pdf`;
    pdf.save(nombre);

    return true;
  } catch (error) {
    console.error('Error descargando PDF:', error);
    throw error;
  }
};

/**
 * Genera PDFs para múltiples productos (archivos separados)
 * @param {Array} productos - Array de productos
 * @param {Object} opciones - Opciones para los PDFs
 */
export const generarPDFsMultiples = async (productos, opciones = {}) => {
  try {
    const pdfs = [];

    for (const producto of productos) {
      const pdf = await generarPDFConQR(producto, {
        ...opciones,
        titulo: `${opciones.titulo || 'Producto'} - ${producto.descripcion || producto.codbar}`
      });
      pdfs.push({
        pdf,
        producto,
        nombre: `producto_${producto.codbar || producto.IdentificadorId || Math.random()}.pdf`
      });
    }

    return pdfs;
  } catch (error) {
    console.error('Error generando PDFs múltiples:', error);
    throw error;
  }
};

/**
 * Genera un solo PDF con múltiples productos (documento unificado)
 * @param {Array} productos - Array de productos
 * @param {Object} opciones - Opciones para el PDF
 * @param {string} [opciones.titulo] - Título del documento
 * @param {boolean} [opciones.mostrarFecha] - Mostrar fecha actual
 * @param {number} [opciones.productosPorPagina] - Productos por página (default: 2)
 * @param {boolean} [opciones.numerarProductos] - Numerar productos (default: true)
 * @param {string} [opciones.encabezado] - Texto adicional en encabezado
 */
export const generarPDFMultiplesUnificado = async (productos, opciones = {}) => {
  try {
    if (!productos || productos.length === 0) {
      throw new Error('No hay productos para generar el PDF');
    }

    // Configuración por defecto
    const config = {
      titulo: opciones.titulo || 'Listado de Productos',
      mostrarFecha: opciones.mostrarFecha !== false,
      productosPorPagina: opciones.productosPorPagina || 2,
      numerarProductos: opciones.numerarProductos !== false,
      encabezado: opciones.encabezado || null,
      // ⬇️ nuevo
      format: opciones.format || 'a4',
      orientation: opciones.orientation || 'portrait',
      unit: opciones.unit || 'mm',
      ...opciones
    };

    /* const pdf = new jsPDF({
      orientation: config.orientation,
      unit: config.unit,
      format: config.format
    }); */

    const { format: formatFinal, orientation: orientFinal } = resolverOrientacionYFormato(config.format, config.orientation);
    const pdf = new jsPDF({
      orientation: orientFinal,
      unit: config.unit,
      format: formatFinal
    });

    // Configurar fuente
    pdf.setFont('helvetica');

    // Dimensiones reales
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const etiquetaChica = Array.isArray(config.format) || pageWidth <= 100;
    const miniEtiqueta = etiquetaChica && pageWidth < 100;

    // Layout base
    const margenIzquierdo = miniEtiqueta ? 3 : 15;
    const margenDerecho = miniEtiqueta ? 3 : 15;
    const margenSuperior = miniEtiqueta ? 3 : 20;
    const anchoLinea = pageWidth - margenIzquierdo - margenDerecho;


    // Alturas reservadas aproximadas
    const headerReserve = miniEtiqueta ? 3 : Math.max(28, pageHeight * 0.16);
    const footerReserve = miniEtiqueta ? 3 : Math.max(20, pageHeight * 0.10);

    const altoUtil = pageHeight - headerReserve - footerReserve;
    const productosPorPagina = miniEtiqueta ? 1 : config.productosPorPagina;
    const altoProducto = (altoUtil - 10) / Math.max(1, productosPorPagina);

    // QR proporcional
    const qrSize = miniEtiqueta
      ? Math.min(28, pageWidth * 0.65)               // QR más grande y centrado para mini
      : (miniEtiqueta ? Math.min(22, pageWidth * 0.35) : Math.min(50, pageWidth * 0.13));

    let paginaActual = 1;
    let productoEnPagina = 0;
    let yPos = miniEtiqueta ? 3 : 20;

    // Función para crear encabezado de página
    const crearEncabezado = (numeroPagina) => {
      if (miniEtiqueta) return margenSuperior;
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(config.titulo, margenIzquierdo, margenSuperior);

      // Información adicional en el encabezado
      let yEncabezado = margenSuperior + 10;

      if (config.mostrarFecha) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const fechaActual = new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
        pdf.text(`Fecha: ${fechaActual}`, margenIzquierdo, yEncabezado);
        yEncabezado += 6;
      }

      if (config.encabezado) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'italic');
        pdf.text(config.encabezado, margenIzquierdo, yEncabezado);
        yEncabezado += 6;
      }

      // Información de página
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Página ${numeroPagina} - Total de productos: ${productos.length}`, margenIzquierdo, yEncabezado);

      // Línea separadora
      yEncabezado += 5;
      pdf.setLineWidth(0.5);
      pdf.line(margenIzquierdo, yEncabezado, margenIzquierdo + anchoLinea, yEncabezado);

      return yEncabezado + 10;
    };

    // Función para crear pie de página
    const crearPiePagina = (numeroPagina) => {
      if (miniEtiqueta) return; // sin pie
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'italic');
      pdf.text('Generado por PISeun - Sistema de Gestión', margenIzquierdo, pageHeight - 15);

      // Número de página en la esquina derecha
      const textoPagina = `${numeroPagina}`;
      const anchoPagina = pdf.getTextWidth(textoPagina);
      pdf.text(textoPagina, margenIzquierdo + anchoLinea - anchoPagina, pageHeight - 15);

      // Línea en el pie
      pdf.setLineWidth(0.3);
      pdf.line(margenIzquierdo, pageHeight - 20, margenIzquierdo + anchoLinea, pageHeight - 20);
    };

    // Función para agregar nueva página si es necesario
    const verificarNuevaPagina = () => {
      if (productoEnPagina >= productosPorPagina) {
        pdf.addPage();
        paginaActual++;
        productoEnPagina = 0;
        yPos = crearEncabezado(paginaActual);
      }
    };

    // Crear primera página
    yPos = crearEncabezado(paginaActual);

    // Procesar cada producto
    for (let i = 0; i < productos.length; i++) {
      const producto = productos[i];
      // Verificar si necesitamos nueva página
      verificarNuevaPagina();

      // Área del producto actual
      //const yInicioProducto = yPos;
      const yFinProducto = yPos + altoProducto - 10;

      // Número del producto (si está habilitado)
      if (config.numerarProductos) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${i + 1}.`, margenIzquierdo, yPos);
        yPos += 2;
      }

      // Generar código QR para este producto
      let qrDataURL = '';
      if (producto.codbar) {
        try {
          qrDataURL = await QRCode.toDataURL(producto.codbar, {
            width: 150,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });
        } catch (qrError) {
          console.error('Error generando QR para producto:', producto.codbar, qrError);
        }
      }

      if (miniEtiqueta) {
        const centerX = pageWidth / 2;
        const qrX = centerX - (qrSize / 2);
        const qrY = yPos;

        if (qrDataURL) {
          pdf.addImage(qrDataURL, 'PNG', qrX, qrY, qrSize, qrSize);
          pdf.setLineWidth(0.3);
          pdf.rect(qrX - 1.5, qrY - 1.5, qrSize + 3, qrSize + 3);
        }

        // Avanzar debajo del QR
        yPos = qrY + qrSize + 5;

        // Texto compacto...
        pdf.setFontSize(8);
        const labelW = 16;
        const labelX = margenIzquierdo;
        const valueX = margenIzquierdo + labelW;

        if (producto.codbar) {
          pdf.setFont('helvetica', 'bold'); pdf.text('Código QR:', labelX, yPos);
          pdf.setFont('helvetica', 'normal'); pdf.text(String(producto.codbar), valueX, yPos);
          yPos += 4.5;
        }

        if (producto.descripcion) {
          pdf.setFont('helvetica', 'bold'); pdf.text('Producto:', labelX, yPos);
          pdf.setFont('helvetica', 'normal'); pdf.text(String(producto.descripcion), valueX, yPos);
          yPos += 4.5;
        }

        if (producto.peso != null) {
          pdf.setFont('helvetica', 'bold'); pdf.text('Unidad:', labelX, yPos);
          pdf.setFont('helvetica', 'normal'); pdf.text(`${producto.peso} ${producto.unidad || ''}`, valueX, yPos);
          yPos += 4.5;
        }

        if (producto.fechaVencimiento) {
          pdf.setFont('helvetica', 'bold'); pdf.text('Vence:', labelX, yPos);
          pdf.setFont('helvetica', 'normal');
          const dt = producto.fechaVencimiento instanceof Date ? producto.fechaVencimiento : parseFechaFlexible(producto.fechaVencimiento);
          pdf.text((dt ? dt.toLocaleDateString('es-ES') : '—'), valueX, yPos);
          yPos += 4.5;
        }

        // Cerrar bloque mini y continuar con el siguiente producto
        productoEnPagina++;
        if (productoEnPagina >= productosPorPagina) {
          // en mini productosPorPagina ya es 1
          if (i < productos.length - 1) {
            pdf.addPage();
            paginaActual++;
            productoEnPagina = 0;
            yPos = crearEncabezado(paginaActual);
          }
        } else {
          // (en caso de querer varias por página en algún otro tamaño chico)
          yPos += 5;
        }

        continue; // <- seguir con el próximo producto
      }


      // Posicionar QR en la esquina derecha del área del producto
      const qrX = margenIzquierdo + anchoLinea - qrSize;
      const qrY = yPos;
      if (qrDataURL) {
        pdf.addImage(qrDataURL, 'PNG', qrX, qrY, qrSize, qrSize);

        // Marco alrededor del QR
        pdf.setLineWidth(0.2);
        pdf.rect(qrX - 1, qrY - 1, qrSize + 2, qrSize + 2);
      }

      // Información del producto (lado izquierdo)
      // const xInfoProducto = config.numerarProductos ? margenIzquierdo + 10 : margenIzquierdo;
      const anchoMaximoTexto = anchoLinea - qrSize - 15;


      pdf.setFontSize(miniEtiqueta ? 8 : 12);
      pdf.setFont('helvetica', 'normal');

      // Código de barras
      if (producto.codbar) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Código:', margenIzquierdo, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(String(producto.codbar), margenIzquierdo + 20, yPos);
        yPos += 5;
      }

      // Código interno
      if (producto.codigo) {
        if (!miniEtiqueta) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Código:', margenIzquierdo, yPos);
          pdf.setFont('helvetica', 'normal');
          pdf.text(String(producto.codigo), margenIzquierdo + 20, yPos);
          yPos += 5;
        }
      }

      // Descripción
      if (producto.descripcion) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Producto:', margenIzquierdo, yPos);
        pdf.setFont('helvetica', 'normal');

        const descripcionTexto = String(producto.descripcion);
        const lineasDescripcion = pdf.splitTextToSize(descripcionTexto, anchoMaximoTexto - 25);

        pdf.text(lineasDescripcion, margenIzquierdo + 20, yPos);
        yPos += Math.max(lineasDescripcion.length * 4, 5);
      }

      // Cantidad y Peso en la misma línea
      // pdf.setFont('helvetica', 'normal');
      // let infoLinea = '';
      /* if (producto.cantidad !== undefined && producto.cantidad !== null) {
        infoLinea += `Bultos: ${producto.cantidad}`;
      } */
      if (producto.peso !== undefined && producto.peso !== null) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Cantidad:', margenIzquierdo, yPos);

        pdf.setFont('helvetica', 'normal');
        pdf.text(`${producto.peso} ${producto.unidad || ''}`, margenIzquierdo + 20, yPos);
        yPos += miniEtiqueta ? 4 : 8;
      }

      const OFFSET = 20;
      pdf.setFontSize(miniEtiqueta ? 8 : 12);

      if (producto.proceso) {
        if (!miniEtiqueta) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Proceso:', margenIzquierdo, yPos);

          pdf.setFont('helvetica', 'normal');
          pdf.text(String(producto.proceso), margenIzquierdo + OFFSET, yPos);
          yPos += 5;
        }
      }

      if (producto.lote) {
        if (!miniEtiqueta) {
          pdf.text(`Lote: ${producto.lote}`, margenIzquierdo, yPos);
          yPos += 5;
        }
      }

      if (producto.fechaVencimiento) {
        const dt = producto.fechaVencimiento instanceof Date
          ? producto.fechaVencimiento
          : parseFechaFlexible(producto.fechaVencimiento);
        const fechaVenc = dt ? dt.toLocaleDateString('es-ES') : '—';

        pdf.setFont('helvetica', 'bold');
        pdf.text('Vence:', margenIzquierdo, yPos);

        pdf.setFont('helvetica', 'normal');
        pdf.text(fechaVenc, margenIzquierdo + OFFSET, yPos);
        yPos += miniEtiqueta ? 4 : 8;
      }

      // Información adicional en líneas compactas
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(miniEtiqueta ? 8 : 10);

      /*  if (producto.lote) {
         pdf.text(`Lote: ${producto.lote}`, xInfoProducto, yPos);
         yPos += 4;
       } */

      /* if (producto.fechaVencimiento) {
        const fechaVenc = new Date(producto.fechaVencimiento).toLocaleDateString('es-ES');
        pdf.text(`Vence: ${fechaVenc}`, xInfoProducto, yPos);
        yPos += 4;
      } */

      if (producto.proveedor) {
        if (!miniEtiqueta) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          const proveedorTexto = typeof producto.proveedor === 'string' ?
            producto.proveedor :
            producto.proveedor.sDescripcion || producto.proveedor.nombre || String(producto.proveedor);
          const proveedorCorto = proveedorTexto.length > 40 ?
            proveedorTexto.substring(0, 37) + '...' : proveedorTexto;
          pdf.text(`Proveedor: ${proveedorCorto}`, margenIzquierdo, yPos);
          yPos += 4;
        }
      }

      // Línea separadora entre productos (excepto el último de la página)
      if (productoEnPagina < productosPorPagina - 1 && i < productos.length - 1) {
        yPos = yFinProducto;
        pdf.setLineWidth(0.2);
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margenIzquierdo, yPos, margenIzquierdo + anchoLinea, yPos);
        pdf.setDrawColor(0, 0, 0); // Restaurar color negro
        yPos += 5;
      } else {
        // Si es el último producto de la página, posicionar para la siguiente
        yPos = yFinProducto + 10;
      }

      productoEnPagina++;
    }

    // Crear pie de página para todas las páginas
    for (let pagina = 1; pagina <= paginaActual; pagina++) {
      if (pagina > 1) {
        pdf.setPage(pagina);
      }
      crearPiePagina(pagina);
    }

    return pdf;

  } catch (error) {
    console.error('Error generando PDF múltiple unificado:', error);
    throw new Error('Error al generar el PDF unificado: ' + error.message);
  }
};

/**
 * Genera y descarga un PDF unificado con múltiples productos
 * @param {Array} productos - Array de productos
 * @param {Object} opciones - Opciones para el PDF
 * @param {string} [nombreArchivo] - Nombre del archivo a descargar
 */
export const descargarPDFMultiplesUnificado = async (productos, opciones = {}, nombreArchivo = null) => {
  try {
    const pdf = await generarPDFMultiplesUnificado(productos, opciones);

    // Generar nombre de archivo si no se proporciona
    const nombre = nombreArchivo ||
      `productos_listado_${productos.length}_items_${new Date().getTime()}.pdf`;

    // Descargar el PDF
    pdf.save(nombre);

    return true;
  } catch (error) {
    console.error('Error descargando PDF múltiple unificado:', error);
    throw error;
  }
};

const parseFechaFlexible = (valor) => {
  if (!valor) return null;
  if (valor instanceof Date) return valor;

  if (typeof valor === 'string') {
    // dd/MM/yyyy o d/M/yyyy
    const m = valor.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
      const d = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10) - 1;
      const y = parseInt(m[3], 10);
      const dt = new Date(y, mo, d);
      return isNaN(dt.getTime()) ? null : dt;
    }
  }

  const dt = new Date(valor);
  return isNaN(dt.getTime()) ? null : dt;
};


/**
 * Función de utilidad para extraer datos del producto desde diferentes formatos
 * @param {Object} item - Item de la lista (puede tener diferentes estructuras)
 * @returns {Object} - Objeto producto normalizado
 */
// ✅ ACTUALIZA el normalizador existente:
export const normalizarProducto = (item) => {
  const fvRaw = item.FechaVencimiento || item.fechaVencimiento || item.FechaVto || item.Vencimiento || null;

  return {

    fechaVencimiento: fvRaw ? parseFechaFlexible(fvRaw) : null,

    codbar:
      item.sCodbar || item.CodBar || item.codbar || item.codigoBarras || null,

    codigo:
      item.sCodigo || item.Codigo || item.codigo || item.MercaderiaCodigo || null,

    descripcion:
      item.sDescripcion || item.MercaderiaDescripcion || item.descripcion || item.nombre || 'Sin descripción',


    cantidad:
      item.nUnidades ?? item.nCantidad ?? item.Cantidad ?? item.Unidades ?? item.cantidad ?? 0,

    peso:
      item.nPesoOriginal ?? item.nPeso ?? item.Peso ?? item.Kilos ?? item.peso ?? 0,

    unidad:
      item.UnidadDescripcion ?? item.Unidad ?? item.unidad ?? null,

    lote:
      item.sLote ?? item.Lote ?? item.lote ?? null,


    fechaVencimiento: fvRaw ? parseFechaFlexible(fvRaw) : null,

    proveedor:
      item.Proveedor ?? item.ProveedorDescripcion ?? item.proveedor ?? null,


    proceso: item.Proceso ?? item.proceso ?? null,

    remito: item.sRemito ?? item.Remito ?? item.remito ?? null,
    identificadorId: item.IdentificadorId ?? item.IndentificadorId ?? item.id ?? null,
  };
};

/*
 * Muestra un PDF en una ventana emergente en lugar de descargarlo
 * @param {Object} producto - Información del producto
 * @param {Object} opciones - Opciones para el PDF
 * @param {string} [titulo] - Título de la ventana emergente
 */
export const mostrarPDFProductoEnVentana = async (producto, opciones = {}, titulo = 'Vista Previa PDF') => {
  try {
    const productoNormalizado = normalizarProducto(producto);
    const pdf = await generarPDFConQR(productoNormalizado, opciones);

    // Generar el PDF como blob
    const pdfBlob = pdf.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);

    // Abrir ventana emergente directamente con el PDF (solo el documento)
    const ventana = window.open(pdfUrl, '_blank',
      'width=900,height=800,scrollbars=yes,resizable=yes,toolbar=yes,location=no,status=no,menubar=yes');

    if (ventana) {
      // Agregar título a la ventana
      ventana.addEventListener('load', function () {
        try {
          ventana.document.title = titulo;
        } catch (e) {
          console.warn('No se pudo cambiar el título de la ventana');
        }
      });

      // Limpiar URL después de un tiempo
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 60000); // 1 minuto

    } else {
      // Fallback si la ventana emergente es bloqueada
      URL.revokeObjectURL(pdfUrl);
      throw new Error('La ventana emergente fue bloqueada por el navegador. Por favor, permite ventanas emergentes y vuelve a intentar.');
    }

    return true;
  } catch (error) {
    console.error('Error mostrando PDF en ventana:', error);
    throw error;
  }
};

/**
 * Muestra un PDF unificado con múltiples productos en ventana emergente
 * @param {Array} productos - Array de productos
 * @param {Object} opciones - Opciones para el PDF
 * @param {string} [titulo] - Título de la ventana emergente
 */
export const mostrarPDFMultiplesEnVentana = async (productos, opciones = {}, titulo = 'Vista Previa - Múltiples Productos') => {
  try {
    const pdf = await generarPDFMultiplesUnificado(productos, opciones);

    // Generar el PDF como blob
    const pdfBlob = pdf.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);

    // Abrir ventana emergente directamente con el PDF (solo el documento)
    const ventana = window.open(pdfUrl, '_blank',
      'width=950,height=900,scrollbars=yes,resizable=yes,toolbar=yes,location=no,status=no,menubar=yes');

    if (ventana) {
      // Agregar título a la ventana
      ventana.addEventListener('load', function () {
        try {
          ventana.document.title = `${titulo} - ${productos.length} productos`;
        } catch (e) {
          console.warn('No se pudo cambiar el título de la ventana');
        }
      });

      // Limpiar URL después de un tiempo
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 60000); // 1 minuto

    } else {
      // Fallback si la ventana emergente es bloqueada
      URL.revokeObjectURL(pdfUrl);
      throw new Error('La ventana emergente fue bloqueada por el navegador. Por favor, permite ventanas emergentes y vuelve a intentar.');
    }

    return true;
  } catch (error) {
    console.error('Error mostrando PDF múltiple en ventana:', error);
    throw error;
  }
};

/**
 * Función de utilidad que permite elegir entre mostrar en ventana o descargar
 * @param {Object|Array} productos - Producto individual o array de productos
 * @param {Object} opciones - Opciones para el PDF
 * @param {boolean} [mostrarEnVentana=false] - Si true, muestra en ventana; si false, descarga
 */
export const procesarPDF = async (productos, opciones = {}, mostrarEnVentana = false) => {
  try {
    const esArray = Array.isArray(productos);

    if (mostrarEnVentana) {
      if (esArray) {
        if (productos.length === 1) {
          return await mostrarPDFProductoEnVentana(productos[0], opciones, 'Vista Previa - Producto Individual');
        } else {
          return await mostrarPDFMultiplesEnVentana(productos, opciones, 'Vista Previa - Múltiples Productos');
        }
      } else {
        return await mostrarPDFProductoEnVentana(productos, opciones, 'Vista Previa - Producto');
      }
    } else {
      if (esArray) {
        if (productos.length === 1) {
          return await descargarPDFProducto(productos[0], opciones);
        } else {
          return await descargarPDFMultiplesUnificado(productos, opciones);
        }
      } else {
        return await descargarPDFProducto(productos, opciones);
      }
    }
  } catch (error) {
    console.error('Error procesando PDF:', error);
    throw error;
  }
};